/**
 * OperationsAutomationPage - Automation/Escalation Dashboard
 * ==========================================================
 * Shows reminders sent, escalations, and pending automation issues.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function OperationsAutomationPage() {
  const [loading, setLoading] = useState(true);
  const [automationData, setAutomationData] = useState(null);

  const loadAutomation = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/automation/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.ok) {
        setAutomationData(res.data);
      } else {
        toast.error("Failed to load automation data");
      }
    } catch (err) {
      console.error("Load automation error:", err);
      toast.error(err.response?.data?.detail || "Failed to load automation");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAutomation();
    // Auto-refresh every 5 minutes
    const interval = setInterval(loadAutomation, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadAutomation]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-10 w-10 text-teal-600 mx-auto mb-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-slate-600">Loading automation status...</p>
        </div>
      </div>
    );
  }

  const healthStatus = automationData?.escalation_health || 'unknown';
  const pending = automationData?.pending_issues || {};
  const today = automationData?.today || {};

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Automation & Escalations</h1>
            <p className="text-slate-500">System alerts, reminders, and pending issues</p>
          </div>
          <div className="flex items-center gap-4">
            <span className={`px-4 py-2 rounded-xl font-semibold text-sm ${
              healthStatus === 'healthy' 
                ? 'bg-green-100 text-green-700' 
                : 'bg-amber-100 text-amber-700'
            }`}>
              Status: {healthStatus === 'healthy' ? 'Healthy' : 'Attention Needed'}
            </span>
            <Button onClick={loadAutomation} variant="outline">Refresh</Button>
          </div>
        </div>

        {/* Today's Activity */}
        <Card>
          <CardHeader>
            <CardTitle>Today's Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-teal-700">{today.reminders_sent || 0}</div>
                <p className="text-sm text-teal-600">Reminders Sent</p>
              </div>
              {Object.entries(today.by_type || {}).map(([type, count]) => (
                <div key={type} className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                  <div className="text-xl font-bold text-slate-700">{count}</div>
                  <p className="text-xs text-slate-500 capitalize">{type.replace(/_/g, ' ')}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Pending Issues */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          
          {/* No Response */}
          <Card className={pending.no_response_jobs > 0 ? 'border-red-200' : ''}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                No Response
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${pending.no_response_jobs > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                {pending.no_response_jobs || 0}
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Jobs assigned &gt;2 hours with no contractor response
              </p>
            </CardContent>
          </Card>

          {/* No Quote */}
          <Card className={pending.no_quote_jobs > 0 ? 'border-amber-200' : ''}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <svg className="h-5 w-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Awaiting Quote
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${pending.no_quote_jobs > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                {pending.no_quote_jobs || 0}
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Jobs accepted &gt;24 hours with no quote submitted
              </p>
            </CardContent>
          </Card>

          {/* Missed Schedule */}
          <Card className={pending.missed_schedule_jobs > 0 ? 'border-red-200' : ''}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Missed Schedule
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${pending.missed_schedule_jobs > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                {pending.missed_schedule_jobs || 0}
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Scheduled jobs past their date and not completed
              </p>
            </CardContent>
          </Card>

          {/* Overdue Compliance */}
          <Card className={pending.overdue_compliance_cases > 0 ? 'border-red-200' : ''}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Overdue Compliance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${pending.overdue_compliance_cases > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                {pending.overdue_compliance_cases || 0}
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Compliance cases past their remedial deadline
              </p>
            </CardContent>
          </Card>

          {/* Expiring Certificates */}
          <Card className={pending.expiring_certificates_30_days > 0 ? 'border-amber-200' : ''}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <svg className="h-5 w-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
                Expiring Certificates
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${pending.expiring_certificates_30_days > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                {pending.expiring_certificates_30_days || 0}
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Certificates expiring within 30 days
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Escalation Info */}
        <Card>
          <CardHeader>
            <CardTitle>Escalation Rules</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-slate-600">
            <div className="flex items-center gap-3">
              <span className="bg-slate-100 px-3 py-1 rounded-full text-xs font-semibold">2 hours</span>
              <span>No response reminder sent to contractor</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="bg-slate-100 px-3 py-1 rounded-full text-xs font-semibold">24 hours</span>
              <span>No quote reminder sent to contractor</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="bg-slate-100 px-3 py-1 rounded-full text-xs font-semibold">Missed</span>
              <span>Admin alert for missed scheduled visits</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="bg-slate-100 px-3 py-1 rounded-full text-xs font-semibold">30/14/7/0 days</span>
              <span>Certificate expiry alerts</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="bg-slate-100 px-3 py-1 rounded-full text-xs font-semibold">14/7 days</span>
              <span>Compliance deadline alerts</span>
            </div>
          </CardContent>
        </Card>

        {/* Last Updated */}
        <div className="text-center text-sm text-slate-400">
          Last updated: {automationData?.timestamp ? new Date(automationData.timestamp).toLocaleString() : 'Unknown'}
        </div>
      </div>
    </div>
  );
}
